import ExcelJS from 'exceljs';
import { boldNumbersInString } from 'core-ui/BoldNumberInString/boldNumbersInString';
import { DateCustomPeriodTypeFormat } from 'helpers/dateFormattingHelper';
import { getHierarchyFiltersLabelData, getAdvancedFiltersLabelData } from 'helpers/utilities';
import { getDateByPeriodByType } from 'helpers/getTimePeriod';
import saveAs from 'file-saver';
import { isDiyWidgetEngineConfig } from 'utils/normalizeWidgetConfig';

// Object with label for hierarchy, advanced and calendar filters
interface FiltersLabel {
  hierarchyFiltersLabel: [];
  advancedFiltersLabel: [];
  label: string;
}

export const exportToExcel = async (
  tableData: any,
  title: string,
  showTotals: boolean,
  customWidgetConfig: any
) => {
  try {
    // Get label for hierarchy, advanced and calendar filters
    const obj: FiltersLabel | undefined = getFiltersLabel(customWidgetConfig);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    const csvData = getDataToExport(
      tableData,
      title,
      showTotals,
      obj?.hierarchyFiltersLabel ? obj?.hierarchyFiltersLabel : [],
      obj?.advancedFiltersLabel ? obj?.advancedFiltersLabel : [],
      obj?.label ? obj?.label : ''
    );

    worksheet.addRows(csvData);

    getMaxWidth(csvData).forEach((column: any, index: number) => {
      worksheet.getColumn(index + 1).width = column.wch;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, `${title || 'data'}.xlsx`);
  } catch (error) {
    console.error('Failed to export Excel file', error);
    throw new Error('Failed to export Excel file');
  }
};

// Method to return label for selected filters
const getFiltersLabel = (customWidgetConfig: any): FiltersLabel | undefined => {
  let label = '';
  let hierarchyFiltersLabel = [];
  let advancedFiltersLabel = [];
  let timePeriodType = '';
  if (customWidgetConfig?.filters != null) {
    if (customWidgetConfig?.trend?.length === 0) {
      const shortLabel = customWidgetConfig?.filters?.currentSelection?.dateFilter;
      timePeriodType = customWidgetConfig?.filters?.currentSelection?.dateFilter?.type;
      const startDate = DateCustomPeriodTypeFormat(
        shortLabel?.startDate ? new Date(shortLabel.startDate) : new Date()
      );
      const endDate = DateCustomPeriodTypeFormat(
        shortLabel?.endDate ? new Date(shortLabel.endDate) : new Date()
      );

      const displayName = shortLabel?.displayName ?? '';
      const safeStartDate = startDate ?? '';
      const safeEndDate = endDate ?? '';

      if (timePeriodType === 'CustomRange') {
        const hasStart = !!safeStartDate;
        const hasEnd = !!safeEndDate;

        let rangePart = '';
        if (hasStart && hasEnd) {
          rangePart = `${safeStartDate} - ${safeEndDate}`;
        } else if (hasStart) {
          rangePart = safeStartDate;
        } else if (hasEnd) {
          rangePart = safeEndDate;
        }

        if (displayName && rangePart) {
          label = `${displayName}: ${rangePart}`;
        } else if (rangePart) {
          label = rangePart;
        } else if (displayName) {
          label = displayName;
        } else {
          label = '';
        }
      } else {
        const periodText = timePeriodType ? getDateByPeriodByType(timePeriodType) ?? '' : '';
        if (displayName && periodText) {
          label = `${displayName}: ${periodText}`;
        } else {
          label = periodText || displayName || '';
        }
      }
    }

    // Get Hierarchy Filters and Advanced Filters Label
    const { hierarchyFilters, advancedFilters } =
      customWidgetConfig.filters?.currentSelection || {};

    hierarchyFiltersLabel = getHierarchyFiltersLabelData(hierarchyFilters);
    advancedFiltersLabel = getAdvancedFiltersLabelData(advancedFilters);
    return {
      hierarchyFiltersLabel,
      advancedFiltersLabel,
      label,
    };
  }
};

export const getDataToExport = (
  tableData: any,
  title: string,
  showTotals: boolean,
  hierarchyFiltersLabel: [],
  advancedFiltersLabel: [],
  dateRangeFilter: any
) => {
  const temp = tableData?.rawData?.columns || [];
  const columns = temp.map((column: any) => ({
    label: column.displayName ? column.displayName : '',
    key: column.field ? column.field : '',
    isPercentage: column.isPercentage ?? false, // Store isPercentage flag
  }));

  const data = tableData?.data || [];

  // Metric is field_1; add the filter tooltip column right after it when any row has one
  const metricIndex = columns.findIndex((column: any) => column.key === 'field_1');
  if (metricIndex !== -1 && data.some((item: any) => item?.filterTooltip)) {
    columns.splice(metricIndex + 1, 0, {
      label: 'Filters',
      key: 'filterTooltip',
      isPercentage: false,
    });
  }

  // Prepare CSV data with titles
  const dataToExport = [
    [title],
    [tableData?.rawData?.subTitle || ''],
    [dateRangeFilter || ''],
    [hierarchyFiltersLabel.join('\n') || ''],
    [advancedFiltersLabel?.join('\n') || '' || ''],
    [boldNumbersInString(tableData?.rawData?.baseSize ?? '', showTotals, true) || ''],
    columns.map((column: any) => column.label), // Headers
    ...data.map((item: any) =>
      columns.map((column: any) => {
        let columnValue = item[column.key] !== undefined ? item[column.key] : '';
        if (column.isPercentage && columnValue !== '-' && !isNaN(Number(columnValue))) {
          columnValue = `${columnValue}%`;
        }
        return columnValue;
      })
    ),
  ];

  return dataToExport;
};

// Function to calculate the maximum width of the content in each column
const getMaxWidth = (data: any) => {
  const maxWidths: any = [];

  data.forEach((row: any) => {
    row.forEach((cell: any, colIndex: any) => {
      if (cell !== undefined) {
        const cellWidth = cell.toString().length;
        maxWidths[colIndex] = Math.max(maxWidths[colIndex] || 0, cellWidth);
      }
    });
  });

  return maxWidths.map((width: any) => ({ wch: width + 2 })); // Add some padding
};

export const convertToCSV = (
  table: any,
  title: string,
  showTotals: boolean,
  customWidgetConfig: any
) => {
  try {
    // Get label for hierarchy, advanced and calendar filters
    const obj: FiltersLabel | undefined = getFiltersLabel(customWidgetConfig);
    const csvData = getDataToExport(
      table,
      title,
      showTotals,
      obj?.hierarchyFiltersLabel ? obj?.hierarchyFiltersLabel : [],
      obj?.advancedFiltersLabel ? obj?.advancedFiltersLabel : [],
      obj?.label ? obj?.label : ''
    );
    const csvRows = [];
    for (const row of csvData) {
      const csvColumns = row.map((value: any) => `"${value}"`);
      csvRows.push(csvColumns.join(','));
    }
    return csvRows.join('\n');
  } catch (error) {
    console.error('Failed to convert data to CSV', error);
    throw new Error('Failed to export CSV file');
  }
};

const filterDataFields = (data: any[], fieldsToRemove: any[]) => {
  return data.map((entry) => {
    fieldsToRemove.forEach((field) => {
      delete entry[field];
    });
    return entry;
  });
};

export const convertFieldObjectToValue = (filteredTable: any, customWidgetConfig: any) => {
  const isDiyWidgetEngine = isDiyWidgetEngineConfig(customWidgetConfig);
  const newData = filteredTable.rawData.data.map((entry: any) => {
    const newEntry: any = {};
    Object.keys(entry).forEach((key) => {
      const f = entry[key];
      if (isDiyWidgetEngine && f && typeof f === 'object' && 'value' in f) {
        const rawValue = f.value;
        const isValidPercentage =
          f.isPercentage && rawValue !== '-' && !isNaN(Number(rawValue)) && rawValue !== '';
        newEntry[key] = isValidPercentage ? `${rawValue}%` : rawValue;
      } else {
        newEntry[key] = f && typeof f === 'object' && 'value' in f ? f.value : f;
      }
    });
    return newEntry;
  });
  filteredTable.rawData.data = newData;
  filteredTable.data = newData;
  return filteredTable;
};

export const removeOmittedColumns = (dataObj: any) => {
  // Step 1: Filter out the columns with omit = true
  const columnsToKeep = dataObj.columns.filter((column: { omit: any }) => !column.omit);

  // Step 2: Identify the sortField values of columns with omit = true
  const sortFieldsToRemove = dataObj.columns
    .filter((column: { omit: any }) => column.omit)
    .map((column: { sortField: any }) => column.sortField);

  // Filter rawData columns
  const rawColumnsToKeep = dataObj.rawData.columns.filter(
    (column: { field: any }) => !sortFieldsToRemove.includes(column.field)
  );

  // Filter rawData data
  const filteredRawData = filterDataFields(dataObj.rawData.data, sortFieldsToRemove);

  // Filter main data
  const filteredMainData = filterDataFields(dataObj.data, sortFieldsToRemove);

  // Update the object with filtered data
  dataObj.columns = columnsToKeep;
  dataObj.rawData.columns = rawColumnsToKeep;
  dataObj.rawData.data = filteredRawData;
  dataObj.data = filteredMainData;

  return dataObj;
};

export const exportToHtml = (tableData: any, title: string) => {
  try {
    const temp = tableData?.columns || [];
    const headers = temp.map((column: any) => ({
      label:
        column.name && column.name.props && column.name.props.text ? column.name.props.text : '',
      key: column.sortField ? column.sortField : '',
    }));
    const data = tableData?.data || [];
    const rows = [
      ...data.map((item: any) =>
        headers.map((column: any) => (item[column.key] !== undefined ? item[column.key] : ''))
      ),
    ];

    const htmlTable = createHtmlTable(headers, rows);

    const fullHtml = `
    <html>
      <head>
        <title>${title}</title>
      </head>
      <body>
        <h1>${title}</h1>
        ${htmlTable}
      </body>
    </html>
  `;

    const blob = new Blob([fullHtml], { type: 'text/html' });
    saveAs(blob, `${title}.html`);
  } catch (error) {
    console.error('Failed to export HTML file', error);
    throw new Error('Failed to export HTML file');
  }
};

export const exportToHtmlDynamicTable = (tableData: any, title: string) => {
  try {
    const temp = tableData?.rawData?.columns || tableData?.columns || [];
    const headers = temp.map((column: any) => ({
      label:
        column.displayName ??
        column.name?.props?.text ??
        column.name ??
        column.field ??
        column.sortField ??
        '',
      key: column.field ?? column.sortField ?? column.key ?? '',
    }));
    const data = tableData?.rawData?.data || tableData?.data || [];
    const rows = data.map((item: any) =>
      headers.map((column: any) => {
        const value = item?.[column.key];
        if (value === undefined || value === null) {
          return '';
        }
        if (typeof value === 'object' && 'value' in value) {
          return value.value ?? '';
        }
        return value;
      })
    );

    const htmlTable = createHtmlTable(headers, rows);

    const fullHtml = `
    <html>
      <head>
        <title>${title}</title>
      </head>
      <body>
        <h1>${title}</h1>
        ${htmlTable}
      </body>
    </html>
  `;

    const blob = new Blob([fullHtml], { type: 'text/html' });
    saveAs(blob, `${title}.html`);
  } catch (error) {
    console.error('Failed to export HTML file', error);
    throw new Error('Failed to export HTML file');
  }
};

const createHtmlTable = (headers: any, rows: any) => {
  let htmlTable = '<table border="1">';
  htmlTable += '<tr>';
  headers.forEach((column: { label: string }) => {
    htmlTable += '<th>' + column.label + '</th>';
  });
  htmlTable += '</tr>';
  rows.forEach((row: any) => {
    htmlTable += '<tr>';
    row.forEach((cell: any) => {
      if (typeof cell === 'object' && 'value') {
        htmlTable += '<td>';
        Object.keys(cell).forEach((key: any) => {
          if (key === 'Id') return;
          htmlTable += '<div>' + key + ': ' + cell[key] + '</div>';
        });
        htmlTable += '</td>';
        return;
      }
      htmlTable += '<td>' + cell + '</td>';
    });
    htmlTable += '</tr>';
  });
  htmlTable += '</table>';
  return htmlTable;
};
